import React from 'react';
import { Loader, NumberInput, Pagination, Select, UnstyledButton } from '@mantine/core';
import type { AdminPaymentlineView } from '../../../services/admin-payments/admin-payments.service';
import {
  METHOD_LABELS,
  ORDER_STATUS_LABELS,
  SORT_LABELS,
  STATUS_LABELS,
  type SortKey,
} from '../monitoring-page.constants';
import { formatDateTime, formatMoney } from '../monitoring-page.utils';
import type { UseMonitoringPage } from '../useMonitoringPage';
import classes from '../monitoring-page.module.scss';

type MonitoringTableProps = Pick<
  UseMonitoringPage,
  | 'paymentlines'
  | 'isLoading'
  | 'hasLoaded'
  | 'sortKey'
  | 'sortDirection'
  | 'toggleSort'
  | 'getDraftForLine'
  | 'applyAndSave'
  | 'openAuditModal'
  | 'totalPages'
  | 'page'
  | 'setPage'
>;

export const MonitoringTable: React.FC<MonitoringTableProps> = ({
  paymentlines,
  isLoading,
  hasLoaded,
  sortKey,
  sortDirection,
  toggleSort,
  getDraftForLine,
  applyAndSave,
  openAuditModal,
  totalPages,
  page,
  setPage,
}) => {
  const renderSortableHeader = (key: SortKey) => {
    const isActive = sortKey === key;
    const indicator = !isActive ? '↕' : sortDirection === 'asc' ? '↑' : '↓';

    return (
      <UnstyledButton
        className={isActive ? `${classes.sortButton} ${classes.sortButtonActive}` : classes.sortButton}
        onClick={() => toggleSort(key)}
        data-testid={`monitoring-payments-sort-${key}`}
      >
        <span>{SORT_LABELS[key]}</span>
        <span className={classes.sortIndicator} aria-hidden="true">
          {indicator}
        </span>
      </UnstyledButton>
    );
  };

  if (isLoading && !hasLoaded) {
    return (
      <div className={classes.loadingWrap}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={classes.tableWrap}>
      <table className={classes.table}>
        <thead>
          <tr>
            <th>{renderSortableHeader('companyName')}</th>
            <th>{renderSortableHeader('sessionName')}</th>
            <th>{renderSortableHeader('orderCode')}</th>
            <th>{renderSortableHeader('tableNumber')}</th>
            <th>{renderSortableHeader('status')}</th>
            <th>{renderSortableHeader('method')}</th>
            <th>{renderSortableHeader('amount')}</th>
            <th>{renderSortableHeader('orderStatus')}</th>
            <th>{'נוצר ע"י'}</th>
            <th>{'נסגר ע"י'}</th>
            <th>{renderSortableHeader('createdAt')}</th>
            <th>{renderSortableHeader('updatedAt')}</th>
          </tr>
        </thead>
        <tbody>
          {paymentlines.map((line) => {
            const draft = getDraftForLine(line);
            const statusClassName = line.status === 'done'
              ? classes.statusDone
              : line.status === 'new'
                ? classes.statusActive
                : classes.statusCanceled;

            return (
              <tr key={line.id} className={classes.clickableRow} onClick={() => void openAuditModal(line)} data-testid={`monitoring-payments-row-${line.id}`}>
                <td data-label={'חברה'}>{line.companyName}</td>
                <td data-label={'משמרת'}>{line.sessionName}</td>
                <td data-label={'הזמנה'}>{line.orderCode}</td>
                <td data-label={'שולחן'}>{line.tableNumber ?? '-'}</td>
                <td data-label={'סטטוס תשלום'}>
                  <div className={classes.cellEditor}>
                    <Select
                      value={draft.status}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(value) => applyAndSave(line, { status: (value as AdminPaymentlineView['status']) ?? line.status })}
                      data={Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }))}
                      allowDeselect={false}
                      size="xs"
                      data-testid={`monitoring-payments-row-status-${line.id}`}
                    />
                    <span className={statusClassName}>{STATUS_LABELS[line.status]}</span>
                  </div>
                </td>
                <td data-label={'אופן תשלום'}>
                  <div className={classes.cellEditor}>
                    <Select
                      value={draft.method}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(value) => applyAndSave(line, { method: (value as AdminPaymentlineView['method']) ?? line.method })}
                      data={Object.entries(METHOD_LABELS).map(([value, label]) => ({ value, label }))}
                      allowDeselect={false}
                      size="xs"
                      data-testid={`monitoring-payments-row-method-${line.id}`}
                    />
                    <span>{METHOD_LABELS[line.method]}</span>
                  </div>
                </td>
                <td data-label={'סכום'}>
                  <div className={classes.cellEditor}>
                    <NumberInput
                      value={draft.amount}
                      min={line.globalTipCollected ?? 0}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(value) => {
                        if (typeof value === 'number') {
                          applyAndSave(line, { amount: value });
                        }
                      }}
                      decimalScale={1}
                      hideControls
                      size="xs"
                      data-testid={`monitoring-payments-row-amount-${line.id}`}
                    />
                    <span>{`${formatMoney(line.amount)} ₪`}</span>
                  </div>
                </td>
                <td data-label={'סטטוס הזמנה'}>{ORDER_STATUS_LABELS[line.orderStatus]}</td>
                <td data-label={'נוצר ע"י'}>{line.createdByName ?? '-'}</td>
                <td data-label={'נסגר ע"י'}>{line.closedByName ?? '-'}</td>
                <td data-label={'נוצר ב'}>{formatDateTime(line.createdAt)}</td>
                <td data-label={'שונה ב'}>{formatDateTime(line.updatedAt)}</td>
              </tr>
            );
          })}
          {paymentlines.length === 0 && (
            <tr>
              <td colSpan={12} className={classes.empty}>
                {'אין שורות תשלום המתאימות לסינון הנוכחי.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {totalPages > 1 && (
        <div className={classes.pagination}>
          <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
        </div>
      )}
    </div>
  );
};
